/**
 * Web Vitals Reporter — Real-time Core Web Vitals monitoring
 * 
 * Tracks LCP, CLS, INP, TTFB, FCP using the web-vitals library.
 * Reports metrics to GA4, GTM dataLayer, and optional beacon endpoint.
 * Includes threshold alerts for poor scores.
 * 
 * Google uses CWV as a ranking signal — this component helps identify
 * performance regressions before they impact search rankings.
 * 
 * @see https://web.dev/articles/vitals
 */
import { useEffect } from 'react';
import { onLCP, onCLS, onINP, onTTFB, onFCP, type Metric } from 'web-vitals';
import { shouldSkipPublicMeasurement } from '@/lib/measurement-hosts';

function isLikelyBotUserAgent(userAgent: string): boolean {
  return /bot|crawler|spider|slurp|bingpreview|mediapartners-google|adsbot|google-inspectiontool|headless|lighthouse|pagespeed|curl|wget|python-requests|httpclient/i.test(userAgent);
}

function shouldSkipVitalsReporting(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return true;
  if (shouldSkipPublicMeasurement(window.location.hostname)) return true;
  return isLikelyBotUserAgent(navigator.userAgent || '');
}

// Thresholds from Google's CWV assessment (2024+, FID replaced by INP)
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },
  CLS: { good: 0.1, poor: 0.25 },
  INP: { good: 200, poor: 500 },
  TTFB: { good: 800, poor: 1800 },
  FCP: { good: 1800, poor: 3000 },
} as const;

function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name as keyof typeof THRESHOLDS];
  if (!threshold) return 'needs-improvement';
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

function reportMetric(metric: Metric) {
  if (shouldSkipVitalsReporting()) return;

  const rating = getRating(metric.name, metric.value);
  const roundedValue = Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value);
  
  // Log to console in development for debugging
  if (import.meta.env.DEV) {
    const color = rating === 'good' ? '#0cce6b' : rating === 'needs-improvement' ? '#ffa400' : '#ff4e42';
    console.log(
      `%c[CWV] ${metric.name}: ${metric.value.toFixed(1)} (${rating})`,
      `color: ${color}; font-weight: bold;`
    );
  }

  // Push to GTM dataLayer for GSC-compatible monitoring
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'web_vitals',
      cwv_metric: metric.name,
      cwv_value: roundedValue,
      cwv_rating: rating,
      cwv_id: metric.id,
      cwv_delta: Math.round(metric.delta),
      cwv_navigation_type: metric.navigationType,
      page_path: window.location.pathname,
      page_language: document.documentElement.lang || 'en',
    });

    // Alert on poor scores — push separate event for monitoring
    if (rating === 'poor') {
      window.dataLayer.push({
        event: 'cwv_poor_score',
        cwv_metric: metric.name,
        cwv_value: roundedValue,
        cwv_threshold: THRESHOLDS[metric.name as keyof typeof THRESHOLDS]?.poor,
        page_path: window.location.pathname,
      });
    }
  }

  // Send to Google Analytics 4 if available
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', metric.name, {
      value: roundedValue,
      event_category: 'Web Vitals',
      event_label: metric.id,
      metric_rating: rating,
      non_interaction: true,
    });
  }

  // Send via sendBeacon to edge function for server-side CWV collection
  if (import.meta.env.PROD && navigator.sendBeacon) {
    const body = JSON.stringify({
      name: metric.name,
      value: metric.value,
      rating,
      delta: metric.delta,
      id: metric.id,
      navigationType: metric.navigationType,
      page: window.location.pathname,
      language: document.documentElement.lang || 'en',
      timestamp: Date.now(),
    });
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    if (projectId) {
      const url = `https://${projectId}.supabase.co/functions/v1/cwv-beacon`;
      // CWV-1910: no cookies — the beacon host's Cloudflare __cf_bm cookie is
      // rejected by Firefox (third-party domain). keepalive keeps unload delivery.
      try {
        fetch(url, { method: 'POST', body, keepalive: true, credentials: 'omit', mode: 'no-cors', headers: { 'Content-Type': 'text/plain' } })
          .catch(() => navigator.sendBeacon(url, body));
      } catch {
        navigator.sendBeacon(url, body);
      }
    }
  }
}

export function WebVitalsReporter() {
  useEffect(() => {
    // Register all CWV observers
    onLCP(reportMetric);
    onCLS(reportMetric);
    onINP(reportMetric);
    onTTFB(reportMetric);
    onFCP(reportMetric);
  }, []);

  return null;
}
